import { useContext, useEffect, useRef, useState } from "react";
import { useQuery } from "urql";
import { GlobalContext } from "../../App";
import AutoCompleteInput from "../AutoCompleteInput";
import { MapBase } from "../MapBase/MapBase";
import { MapMarker } from "../MapMarker";
import { SkinDataQuery } from "../../queries/SkinDataQuery";
import { ClinicCard } from "../ClinicCard/ClinicCard";
import { AnimatePresence, LayoutGroup } from "framer-motion/dist/framer-motion";
import { Arrow } from "../Icon";

import "./Page.css";
import { useWindowHeight, useWindowWidth } from "@react-hook/window-size";

export const Page = () => {
  const {
    center,
    allClinics,
    setAllClinics,
    setSkinProblems,
    visibleClinicCardIDs,
    selectedFilterSkinConditions,
    inputValue,
    setSearchValueCoordinates,
    value,
    footerHeight,
    setFooterHeight,
    isOverlayVisible,
    setUserGPSCoordinates,
    setCenter,
    setZoom,
  } = useContext(GlobalContext);

  const [hasLoaded, setHasLoaded] = useState(false);
  const [visibleCardIndexes, setVisibleCardIndexes] = useState([]);
  const [isScrolledDown, setIsScrolledDown] = useState(false);

  const [{ data, fetching, error }] = useQuery({
    query: SkinDataQuery,
    pause: hasLoaded,
  });

  const handleScroll = () => {
    const container = resultsContainerRef.current;
    if (container) {
      setIsScrolledDown(container.scrollTop > 0);
    }
  };

  useEffect(() => {
    const container = resultsContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const resultsContainerRef = useRef(null);
  const clinicCardRefs = useRef([]);

  const windowHeight = useWindowHeight();
  const windowWidth = useWindowWidth();

  // This function checks how many full cards are currently in view
  const checkFullCardsInView = () => {
    const container = resultsContainerRef.current;
    const cardRefs = clinicCardRefs.current;
    let inViewIndexes = [];

    Object.entries(cardRefs).forEach(([id, card], index) => {
      if (card && container) {
        const cardRect = card.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        // Check if the entire card is within the container's view
        if (
          cardRect.top >= containerRect.top &&
          cardRect.bottom <= containerRect.bottom
        ) {
          inViewIndexes.push(parseInt(index));
        }
      }
    });

    setVisibleCardIndexes(inViewIndexes);
    // console.log(`Cards in full view: ${inViewIndexes.join(", ")}`);
  };

  useEffect(() => {
    // Call the function when the list of visible cards changes
    checkFullCardsInView();
    // Add event listener to handle scroll
    const container = resultsContainerRef.current;
    if (container) {
      container.addEventListener("scroll", checkFullCardsInView);
    }

    // Clean up event listener
    return () => {
      if (container) {
        container.removeEventListener("scroll", checkFullCardsInView);
      }
    };
  }, [visibleClinicCardIDs]);

  useEffect(() => {
    if (data && !fetching && !error) {
      setHasLoaded(true); // Set hasLoaded to true after the first successful load
      const { skinClinics, skinLocationCategories } = data;
      setAllClinics(skinClinics.nodes);
      setSkinProblems(skinLocationCategories.nodes.map((n) => n.name));
    }
  }, [data, setAllClinics, setSkinProblems, fetching, error]);

  useEffect(() => {
    if (!inputValue) {
      setSearchValueCoordinates(null);
    }
  }, [inputValue, setSearchValueCoordinates]);

  useEffect(() => {
    if (document.querySelector(".c-footer__left")) {
      setFooterHeight(
        document.querySelector(".c-footer__left").clientHeight + 45
      );
    }
  }, [windowWidth, windowHeight, setFooterHeight]);

  useEffect(() => {
    if (!isOverlayVisible) {
      if (!navigator.geolocation) {
        console.log("Geolocation is not supported by your browser");
      } else {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setUserGPSCoordinates({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
            if (!value && !center) {
              setCenter({
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              });

              setZoom(8);
            }
          },
          (err) => console.error("could not set location", err)
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOverlayVisible]);

  const handleSeeMore = () => {
    const container = resultsContainerRef.current;
    if (isScrolledDown) {
      // Scroll back to top
      container.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      // Loop through the clinicCardRefs to find the next card to scroll to
      let found = false;
      for (let id of visibleClinicCardIDs) {
        const card = clinicCardRefs.current[id];
        if (card) {
          const cardRect = card.getBoundingClientRect();
          const containerRect =
            resultsContainerRef.current.getBoundingClientRect();

          // Check if the top of the card is below the bottom of the container, indicating it's not fully in view
          if (cardRect.top >= containerRect.bottom) {
            // Scroll the card into view and stop the loop
            card.scrollIntoView({ behavior: "smooth", block: "nearest" });
            found = true;
            break;
          }
        }
      }

      // If no card to scroll to was found, optionally handle this scenario (e.g., scroll to bottom or show a message)
      if (!found) {
        console.log("No more clinics to scroll to");
        // You can add additional logic here, like scrolling to the bottom of the container
        resultsContainerRef.current.scrollTo({
          top: resultsContainerRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }
  };

  return (
    <>
      <div className="floating-search-wrapper">
        <AutoCompleteInput />
        <div
          className="results"
          ref={resultsContainerRef}
          style={{ maxHeight: windowHeight - footerHeight - 150 - 48 }}
        >
          <div className="results-container">
            <LayoutGroup>
              <AnimatePresence>
                {visibleClinicCardIDs?.map((id) => {
                  const clinic = allClinics.find((n) => n.id === id);
                  if (clinic) {
                    return (
                      <ClinicCard
                        layout
                        key={clinic.id}
                        clinic={clinic}
                        ref={(el) => {
                          // Add or remove refs dynamically
                          if (el) {
                            clinicCardRefs.current[id] = el;
                          } else {
                            delete clinicCardRefs.current[id];
                          }
                        }}
                      />
                    );
                  }
                  return null;
                })}
              </AnimatePresence>
            </LayoutGroup>
          </div>
        </div>
        {visibleCardIndexes?.length < visibleClinicCardIDs?.length ? (
          <button className="scroll-for-more" onClick={handleSeeMore}>
            <p>{isScrolledDown ? "Back to top" : "See more clinics"}</p>
            <Arrow
              className={`${isScrolledDown ? "up" : "down"} stroke-white`}
            />
          </button>
        ) : null}
      </div>

      <MapBase>
        {allClinics
          ?.filter((clinic) => {
            // only filter if there is a selected filter
            if (selectedFilterSkinConditions.length) {
              return clinic?.skinLocationCategories?.nodes
                ?.map((n) => n.name)
                .some((n) => selectedFilterSkinConditions.indexOf(n) !== -1);
            } else {
              // if there's no selected skin condition, just continue
              return true;
            }
          })
          ?.map((clinic) => (
            <MapMarker
              key={clinic.id}
              lat={clinic.acfData.address.latitude}
              lng={clinic.acfData.address.longitude}
            />
          ))}
      </MapBase>
    </>
  );
};
